import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Truck, FlaskConical, Stethoscope, LineChart, Handshake, Pill, Microscope, Syringe, HeartPulse } from 'lucide-react';
import axios from 'axios';
import { useLang } from '@/i18n/LangContext';
import Reveal from '@/components/Reveal';
import { MoleculeMark, CertBadge } from '@/components/PharmaMarks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREA_ORDER = ['cardiology','neurology','antibiotics','gastro','pediatrics','womens_health','oncology_support','respiratory'];

const SERVICE_ICONS = [FlaskConical, Stethoscope, Handshake, Truck, ShieldCheck, LineChart];
const AREA_ICONS = { cardiology: HeartPulse, neurology: Microscope, antibiotics: Pill, gastro: Pill, pediatrics: Syringe, womens_health: HeartPulse, oncology_support: Microscope, respiratory: Pill };

export default function Home() {
  const { t, areaLabel } = useLang();
  const [stats, setStats] = useState({ products: 60, therapeutic_areas: 8, partner_pharmacies_clinics: 450, languages: 3 });
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/therapeutic-areas`).then((r) => setAreas(r.data)).catch(() => {});
  }, []);

  const areaCount = (id) => areas.find((a) => a.id === id)?.count ?? 0;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="mm-hero" data-testid="hero">
        <div className="molecule-bg">
          <MoleculeMark size={520} opacity={0.05} />
        </div>
        <div className="container-mm" style={{ position: 'relative', zIndex: 1 }}>
          <div className="grid">
            <Reveal>
              <div className="mm-hero-badge" data-testid="hero-badge">
                <span className="rx">Rx</span>
                {t.hero.badge}
              </div>
              <div className="eyebrow" data-testid="hero-eyebrow">{t.hero.eyebrow}</div>
              <h1 data-testid="hero-title">
                {t.hero.title_a} <em>{t.hero.title_b}</em><br />
                {t.hero.title_c}
              </h1>
              <p className="lead">{t.hero.lead}</p>
              <div className="ctas">
                <Link to="/contacts" className="btn btn-primary" data-testid="hero-cta-partner">
                  {t.hero.cta_partner} <ArrowUpRight size={16} />
                </Link>
                <Link to="/services" className="btn btn-ghost" data-testid="hero-cta-services">
                  {t.hero.cta_services}
                </Link>
              </div>
            </Reveal>

            <Reveal delay={140}>
              <div className="mm-hero-visual" data-testid="hero-visual">
                <img
                  src="https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=1200&q=70"
                  alt="Pharmaceutical quality — macro"
                />
                <div className="compliance-strip">
                  <span><span className="dot" />{t.hero.compliance_status}</span>
                  <span>GDP · GMP</span>
                </div>
                <div className="tag">{t.hero.tag}</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CERT BAR */}
      <section className="mm-certs" data-testid="cert-bar" aria-label={t.certs.title}>
        <CertBadge code={t.certs.gmp.label} label="Manufacturing" hint={t.certs.gmp.hint} />
        <CertBadge code={t.certs.gdp.label} label="Distribution"  hint={t.certs.gdp.hint} />
        <CertBadge code={t.certs.iso.label} label="Management"    hint={t.certs.iso.hint} />
        <CertBadge code={t.certs.pv.label}  label="Safety"        hint={t.certs.pv.hint}  />
      </section>

      {/* STATS */}
      <section className="mm-stats" data-testid="stats">
        <div className="container-mm">
          <div className="grid">
            <div className="stat">
              <div className="num"><em>{stats.products}</em>+</div>
              <div className="label">{t.stats.products}</div>
            </div>
            <div className="stat">
              <div className="num"><em>{stats.therapeutic_areas}</em></div>
              <div className="label">{t.stats.areas}</div>
            </div>
            <div className="stat">
              <div className="num"><em>{stats.partner_pharmacies_clinics}</em>+</div>
              <div className="label">{t.stats.partners}</div>
            </div>
            <div className="stat">
              <div className="num"><em>{stats.languages}</em></div>
              <div className="label">TJ · RU · EN</div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT teaser */}
      <section className="mm-section" data-testid="section-about-teaser">
        <div className="container-mm">
          <div className="mm-section-head">
            <div>
              <div className="eyebrow">{t.home.about_eyebrow}</div>
            </div>
            <div>
              <h2>{t.home.about_title}</h2>
              <p style={{ marginTop: 24 }}>{t.home.about_lead}</p>
              <Link to="/about" className="btn btn-ghost" style={{ marginTop: 24 }} data-testid="about-teaser-cta">
                {t.home.about_cta} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mm-section alabaster" data-testid="section-services">
        <div className="container-mm">
          <div className="mm-section-head">
            <div>
              <div className="eyebrow">{t.home.services_eyebrow}</div>
              <h2 style={{ marginTop: 12 }}>{t.home.services_title}</h2>
            </div>
            <div><p>{t.home.services_lead}</p></div>
          </div>

          <div className="mm-features">
            {t.services.map((s, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <Reveal key={i} delay={i * 60} className={i < 2 ? 'mm-col wide' : 'mm-col third'}>
                  <div className="mm-feature" style={{ height: '100%' }}>
                    <div className="icon-wrap"><Icon size={20} /></div>
                    <h3>{s.t}</h3>
                    <p>{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="mm-section" data-testid="section-areas">
        <div className="container-mm">
          <div className="mm-section-head">
            <div>
              <div className="eyebrow">{t.home.areas_eyebrow}</div>
              <h2 style={{ marginTop: 12 }}>{t.home.areas_title}</h2>
            </div>
            <div><p>{t.home.areas_lead}</p></div>
          </div>
          <div className="mm-areas" data-testid="areas-grid">
            {AREA_ORDER.map((id) => {
              const Icon = AREA_ICONS[id] || Pill;
              return (
                <Link to={`/products?area=${id}`} key={id} className="mm-area" data-testid={`area-tile-${id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="mm-area-count">{String(areaCount(id)).padStart(2, '0')} · {t.products.inn}</div>
                    <Icon size={20} style={{ opacity: 0.55 }} />
                  </div>
                  <div className="mm-area-name">{areaLabel(id)}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUALITY (dark) */}
      <section className="mm-section dark grain" data-testid="section-quality" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="container-mm">
          <div className="mm-section-head">
            <div>
              <div className="eyebrow">{t.home.quality_eyebrow}</div>
              <h2 style={{ marginTop: 12 }}>{t.home.quality_title}</h2>
            </div>
            <div><p>{t.home.quality_lead}</p></div>
          </div>

          <div className="mm-features">
            {t.quality.items.map((q, i) => (
              <Reveal key={i} delay={i * 80} className="mm-col third">
                <div className="mm-feature" style={{ height: '100%' }}>
                  <div className="icon-wrap"><ShieldCheck size={20} /></div>
                  <h3>{q.t}</h3>
                  <p>{q.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW (dark process strip inside) */}
      <section className="mm-section dark" style={{ paddingTop: 0 }} data-testid="section-how">
        <div className="container-mm">
          <div className="mm-section-head">
            <div>
              <div className="eyebrow">{t.home.how_eyebrow}</div>
              <h2 style={{ marginTop: 12 }}>{t.home.how_title}</h2>
            </div>
            <div />
          </div>
          <div className="mm-process">
            {t.steps.map((s, i) => (
              <div className="mm-step" key={i} data-testid={`step-${i+1}`}>
                <div className="num">STEP · {String(i + 1).padStart(2, '0')}</div>
                <h4>{s.t}</h4>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GEOGRAPHY */}
      <section className="mm-section alabaster" data-testid="section-geo">
        <div className="container-mm">
          <div className="mm-section-head">
            <div>
              <div className="eyebrow">{t.home.geo_eyebrow}</div>
              <h2 style={{ marginTop: 12 }}>{t.home.geo_title}</h2>
            </div>
            <div><p>{t.home.geo_lead}</p></div>
          </div>
          <div className="mm-geo">
            <div className="mm-geo-visual" data-testid="home-geo-map">
              <iframe
                title="MED MIR — Dushanbe, Tajikistan"
                src="https://www.google.com/maps?q=Dushanbe,Tajikistan&z=6&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Душанбе (HQ)', 'Худжанд', 'Бохтар', 'Куляб', 'Хорог'].map((city, i) => (
                  <li key={i} style={{
                    padding: '18px 0', borderBottom: '1px solid var(--mm-line)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }} data-testid={`geo-city-${i}`}>
                    <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26 }}>{city}</span>
                    <span className="eyebrow" style={{ color: 'var(--mm-slate)' }}>ТАДЖИКИСТАН</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mm-section" data-testid="section-final-cta">
        <div className="container-mm">
          <div style={{
            background: 'var(--mm-navy)', color: 'var(--mm-ivory)',
            padding: '72px 48px', borderRadius: 4,
            display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 48, alignItems: 'center'
          }}>
            <div>
              <div className="eyebrow" style={{ color: 'var(--mm-amber-2)' }}>PARTNERSHIP</div>
              <h2 style={{ color: 'var(--mm-ivory)', marginTop: 12 }}>{t.home.cta_final_title}</h2>
              <p style={{ color: 'rgba(246,242,235,0.7)', marginTop: 20, maxWidth: 560 }}>{t.home.cta_final_lead}</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Link to="/contacts" className="btn btn-amber" data-testid="final-cta-btn">
                {t.home.cta_final_btn} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
